import { Link } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { StatsSkeleton } from '@/components/PageSkeleton';
import { ErrorState, Metric, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ListBody } from '@/components/inputs';
import Reminders from '@/components/admin/Reminders';
import { ORDER_STATUS_STYLES, PRIORITY_STYLES } from '@/shared/constants';
import { ORDER_LIST_FIELDS, TICKET_FIELDS, aggOf } from '@/shared/fields';
import { money, orderTotal, planSummary, timeLeft, RENEW_WARN_DAYS } from '@/shared/pricing';
import { displayNameOf, rows } from '@/shared/data';
import { formatRelativeTime } from '@/utils/date';

const { eq, and, gt, gte, lte, lt, isIn, isNotNull } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'MoonStar', label: 'Tonight', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 10 };

const COUNT = [{ field: 'Id', fn: 'Count', alias: 'count' }];
const DAY = 86400000;
const iso = (d) => d.toISOString().slice(0, 19);

function bucketsOf(res) {
  if (!res?.success) throw new Error(res?.message || 'Could not load the numbers');
  return aggOf(res);
}

async function loadMetrics() {
  const now = new Date();
  const soon = new Date(now.getTime() + RENEW_WARN_DAYS * DAY);
  const monthAgo = iso(new Date(now.getTime() - 30 * DAY)).slice(0, 10);

  const [orders, tickets, contact, payments] = await Promise.all([
    sdk
      .table('orders_c')
      .select([{ field: 'Id' }])
      .aggregate([
        { id: 'pending', fields: COUNT, where: eq('status_c', 'Pending') },
        { id: 'invoiced', fields: COUNT, where: eq('status_c', 'Invoiced') },
        { id: 'trial', fields: COUNT, where: eq('status_c', 'Trial') },
        { id: 'active', fields: COUNT, where: eq('status_c', 'Active') },
        {
          id: 'expiring',
          fields: COUNT,
          where: and(eq('status_c', 'Active'), gte('ends_at_c', iso(now)), lte('ends_at_c', iso(soon))),
        },
        { id: 'lapsed', fields: COUNT, where: and(eq('status_c', 'Active'), lt('ends_at_c', iso(now))) },
        {
          id: 'renewals',
          fields: COUNT,
          where: and(isIn('status_c', ['Active', 'Trial']), isNotNull('renew_requested_at_c')),
        },
      ])
      .limit(1)
      .fetch(),
    sdk
      .table('tickets_c')
      .select([{ field: 'Id' }])
      .aggregate([{ id: 'open', fields: COUNT, where: eq('status_c', 'Open') }])
      .limit(1)
      .fetch(),
    sdk
      .table('contact_messages_c')
      .select([{ field: 'Id' }])
      .aggregate([{ id: 'new', fields: COUNT, where: eq('status_c', 'New') }])
      .limit(1)
      .fetch(),
    sdk
      .table('payments_c')
      .select([{ field: 'Id' }])
      .aggregate([
        { id: 'revenue', fields: [{ field: 'amount_c', fn: 'Sum', alias: 'count' }], where: gte('paid_on_c', monthAgo) },
      ])
      .limit(1)
      .fetch(),
  ]);

  const o = bucketsOf(orders);
  const t = bucketsOf(tickets);
  const c = bucketsOf(contact);
  const p = bucketsOf(payments);
  return {
    pending: o('pending'),
    invoiced: o('invoiced'),
    trial: o('trial'),
    active: o('active'),
    expiring: o('expiring'),
    lapsed: o('lapsed'),
    renewals: o('renewals'),
    open: t('open'),
    messages: c('new'),
    revenue: p('revenue'),
  };
}

const TONIGHT = [
  {
    id: 'pending',
    label: 'Pending orders',
    hint: 'Waiting for an invoice or a trial',
    icon: 'Inbox',
    tone: 'text-warning',
    to: '/admin/orders?status=Pending',
  },
  {
    id: 'open',
    label: 'Open tickets',
    hint: 'Customers waiting on a reply',
    icon: 'LifeBuoy',
    tone: 'text-primary',
    to: '/admin/tickets?status=Open',
  },
  {
    id: 'expiring',
    label: 'Ending in 7 days',
    hint: 'Active lines about to run out',
    icon: 'Hourglass',
    tone: 'text-highlight',
    to: '/admin/orders?view=expiring',
  },
  {
    id: 'lapsed',
    label: 'Lapsed lines',
    hint: 'Ended and not renewed yet',
    icon: 'Skull',
    tone: 'text-destructive',
    to: '/admin/orders?view=lapsed',
  },
];

function Queue({ title, icon, to, linkLabel, state, empty, children }) {
  const list = state.data ?? [];
  return (
    <Panel
      title={title}
      icon={icon}
      bodyClassName="py-1"
      actions={
        <Button variant="ghost" size="sm" asChild>
          <Link to={to}>
            {linkLabel}
            <ApperIcon name="ArrowRight" size={16} />
          </Link>
        </Button>
      }
    >
      <ListBody
        loading={state.loading}
        data={state.data}
        error={state.error}
        run={state.run}
        rows={3}
        isEmpty={!list.length}
        empty={empty}
      >
        <ul className="divide-y divide-border">{children(list)}</ul>
      </ListBody>
    </Panel>
  );
}

function OrderRow({ record, meta }) {
  return (
    <li>
      <Link
        to={`/admin/orders/${record.Id}`}
        className="flex items-center justify-between gap-3 py-2.5 transition-colors duration-(--transition-fast) hover:text-primary"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium">
            {record.full_name_c || record.customer_c?.Name || record.email_c}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            <span className="font-mono">{record.Name}</span> · {planSummary(record)}
          </span>
        </span>
        <span className="shrink-0 text-right text-xs text-muted-foreground">{meta}</span>
      </Link>
    </li>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isOwner = user?.profile === 'Admin_c';

  const metrics = useFetch(loadMetrics, []);

  const pending = useFetch(
    () =>
      sdk
        .table('orders_c')
        .select(ORDER_LIST_FIELDS)
        .where(isIn('status_c', ['Pending', 'Invoiced']))
        .orderBy('CreatedOn')
        .limit(6)
        .fetch()
        .then(rows),
    [],
  );

  const expiring = useFetch(() => {
    const now = new Date();
    const soon = new Date(now.getTime() + RENEW_WARN_DAYS * DAY);
    return sdk
      .table('orders_c')
      .select(ORDER_LIST_FIELDS)
      .where(and(isIn('status_c', ['Active', 'Trial']), gte('ends_at_c', iso(now)), lte('ends_at_c', iso(soon))))
      .orderBy('ends_at_c')
      .limit(6)
      .fetch()
      .then(rows);
  }, []);

  const tickets = useFetch(
    () =>
      sdk
        .table('tickets_c')
        .select(TICKET_FIELDS)
        .where(eq('status_c', 'Open'))
        .orderByDesc('last_reply_at_c')
        .limit(6)
        .fetch()
        .then(rows),
    [],
  );

  const gifts = useFetch(
    () =>
      sdk
        .table('orders_c')
        .select(ORDER_LIST_FIELDS)
        .where(gt('gift_months_c', 0))
        .orderBy('ends_at_c')
        .limit(6)
        .fetch()
        .then(rows),
    [],
  );
  const giftRows = gifts.data ?? [];

  function refreshAll() {
    gifts.run();
    metrics.run();
    pending.run();
    expiring.run();
    tickets.run();
  }

  const m = metrics.data;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Back office"
        title={`Evening, ${displayNameOf(user).split(' ')[0]}`}
        description="Orders to invoice, lines to light up and customers waiting on a reply."
        actions={
          <>
            {isOwner && <Reminders onDone={refreshAll} />}
            <Button asChild>
              <Link to="/admin/orders/new">
                <ApperIcon name="Plus" size={16} />
                New order
              </Link>
            </Button>
          </>
        }
      />

      <section className="mb-6 overflow-hidden rounded-lg border border-highlight/40 bg-card text-card-foreground shadow-sm">
        <div className="flex flex-col gap-1 border-b border-border px-5 py-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-highlight">Tonight</p>
            <h2 className="font-heading text-2xl leading-tight">Before the lights go out</h2>
          </div>
          {m && (
            <p className="text-sm text-muted-foreground">
              {m.active} active lines · {m.trial} on trial
            </p>
          )}
        </div>
        {metrics.loading && m === undefined ? (
          <div className="p-4">
            <StatsSkeleton />
          </div>
        ) : metrics.error ? (
          <div className="p-4">
            <ErrorState message={metrics.error} onRetry={metrics.run} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-px bg-border lg:grid-cols-4">
            {TONIGHT.map((t) => (
              <Link
                key={t.id}
                to={t.to}
                className="flex min-h-28 flex-col gap-1.5 bg-card p-4 transition-colors duration-(--transition-fast) hover:bg-accent hover:text-accent-foreground"
              >
                <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  <ApperIcon name={t.icon} size={16} className={t.tone} />
                  {t.label}
                </span>
                <span className="font-heading text-4xl leading-none">{m?.[t.id] ?? 0}</span>
                <span className="text-xs text-muted-foreground">{t.hint}</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {m && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric label="Invoiced" value={m.invoiced} hint="Awaiting payment" icon="ReceiptText" tone="text-highlight" />
          <Metric label="Renewals asked" value={m.renewals} hint="Customers who want another term" icon="RefreshCw" />
          <Metric label="New messages" value={m.messages} hint="From the contact form" icon="Mail" tone="text-warning" />
          <Metric label="Last 30 days" value={money(m.revenue)} hint="Payments recorded" icon="Banknote" tone="text-success" />
        </div>
      )}

      {giftRows.length > 0 && (
        <Panel title="Free months to gift" icon="Gift" className="mb-6" bodyClassName="py-1">
          <p className="pt-2 text-xs text-muted-foreground">
            These customers referred a friend who bought 6 months or longer. Open the line to gift the month.
          </p>
          <ul className="divide-y divide-border">
            {giftRows.map((o) => (
              <OrderRow
                key={o.Id}
                record={o}
                meta={
                  <span className="font-semibold text-highlight">
                    {o.gift_months_c} month{Number(o.gift_months_c) === 1 ? '' : 's'} owed
                  </span>
                }
              />
            ))}
          </ul>
        </Panel>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Queue
          title="Waiting on you"
          icon="Inbox"
          to="/admin/orders?status=Pending"
          linkLabel="All"
          state={pending}
          empty={{ icon: 'Inbox', title: 'Inbox zero', description: 'No pending or invoiced orders.' }}
        >
          {(list) =>
            list.map((o) => (
              <OrderRow
                key={o.Id}
                record={o}
                meta={
                  <>
                    <StatusBadge value={o.status_c} styles={ORDER_STATUS_STYLES} />
                    <span className="mt-1 block">{money(orderTotal(o))}</span>
                  </>
                }
              />
            ))
          }
        </Queue>

        <Queue
          title="Ending soon"
          icon="Hourglass"
          to="/admin/orders?view=expiring"
          linkLabel="All"
          state={expiring}
          empty={{ icon: 'Hourglass', title: 'No lines ending this week', description: 'Nothing runs out in the next 7 days.' }}
        >
          {(list) =>
            list.map((o) => (
              <OrderRow
                key={o.Id}
                record={o}
                meta={
                  <>
                    <span className="block text-foreground">{timeLeft(o.ends_at_c)}</span>
                    {o.renew_requested_at_c && <span className="block text-primary">Renewal asked</span>}
                  </>
                }
              />
            ))
          }
        </Queue>

        <Queue
          title="Open tickets"
          icon="LifeBuoy"
          to="/admin/tickets?status=Open"
          linkLabel="All"
          state={tickets}
          empty={{ icon: 'LifeBuoy', title: 'Quiet night', description: 'No tickets are waiting on a reply.' }}
        >
          {(list) =>
            list.map((t) => (
              <li key={t.Id}>
                <Link
                  to={`/admin/tickets/${t.Id}`}
                  className="flex items-center justify-between gap-3 py-2.5 transition-colors duration-(--transition-fast) hover:text-primary"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{t.Name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{t.email_c}</span>
                  </span>
                  <span className="shrink-0 text-right text-xs text-muted-foreground">
                    <StatusBadge value={t.priority_c} styles={PRIORITY_STYLES} />
                    <span className="mt-1 block">
                      {t.last_reply_at_c ? formatRelativeTime(t.last_reply_at_c) : formatRelativeTime(t.CreatedOn)}
                    </span>
                  </span>
                </Link>
              </li>
            ))
          }
        </Queue>
      </div>
    </div>
  );
}
